<script>
import { formatTime } from '@chatwoot/utils';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import FilterSelector from './components/FilterSelector.vue';
import ReportLoadingContent from './components/ReportLoadingContent.vue';
import ReportDownloadMenu from './components/ReportDownloadMenu.vue';

export default {
  name: 'AgentComparisonReports',
  components: {
    FilterSelector,
    ReportLoadingContent,
    UserAvatarWithName,
    ReportDownloadMenu,
  },
  emits: ['agentSelect'],
  data() {
    return {
      from: null,
      to: null,
      businessHours: false,
      comunicanteCampaign: 'all',
    };
  },
  computed: {
    ...mapGetters('agents', {
      agents: 'getAgents',
    }),
    ...mapGetters('summaryReports', {
      summaryReports: 'getAgentSummaryReports',
      summaryReportUiFlags: 'getUIFlags',
    }),
    isFetching() {
      return this.summaryReportUiFlags.isFetchingAgentSummaryReports;
    },
    isDownloadDisabled() {
      return this.isFetching || !this.from || !this.to;
    },
    rows() {
      return this.agents
        .map(agent => {
          const metrics =
            this.summaryReports.find(
              report => report.id === Number(agent.id)
            ) || {};
          return {
            agent,
            conversationsCount: metrics.conversationsCount || 0,
            avgFirstResponseTime: metrics.avgFirstResponseTime,
            avgResolutionTime: metrics.avgResolutionTime,
            avgReplyTime: metrics.avgReplyTime,
            resolvedConversationsCount: metrics.resolvedConversationsCount || 0,
          };
        })
        .sort((first, second) => {
          const countDifference =
            second.conversationsCount - first.conversationsCount;
          return (
            countDifference || first.agent.name.localeCompare(second.agent.name)
          );
        });
    },
    metricColumns() {
      return [
        'CONVERSATIONS',
        'AVG_FIRST_RESPONSE_TIME',
        'AVG_RESOLUTION_TIME',
        'AVG_REPLY_TIME',
        'RESOLUTION_COUNT',
      ];
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
  },
  methods: {
    onFilterChange({ from, to, businessHours, selectedComunicanteCampaign }) {
      this.from = from;
      this.to = to;
      this.businessHours = businessHours;
      this.comunicanteCampaign = selectedComunicanteCampaign.id;
      this.fetchReports();
    },
    fetchReports() {
      return this.$store
        .dispatch('summaryReports/fetchAgentSummaryReports', {
          since: this.from,
          until: this.to,
          businessHours: this.businessHours,
          comunicanteCampaign: this.comunicanteCampaign,
        })
        .catch(() => useAlert(this.$t('REPORT.SUMMARY_FETCHING_FAILED')));
    },
    downloadXlsx() {
      return this.$store.dispatch('downloadEntityReportXlsx', {
        type: 'agents',
        from: this.from,
        to: this.to,
        businessHours: this.businessHours,
        comunicanteCampaign: this.comunicanteCampaign,
        fileName: 'relatorio-de-agentes.xlsx',
      });
    },
    formattedDuration(value) {
      return value ? formatTime(value) : '--';
    },
    agentCellUser(agent) {
      return {
        name: agent.name,
        thumbnail: agent.thumbnail,
        email: agent.email,
      };
    },
  },
};
</script>

<template>
  <div class="flex-1 p-4 overflow-auto">
    <div class="flex flex-col gap-3 md:flex-row md:items-start">
      <div class="flex-1">
        <FilterSelector
          :show-conversation-direction-filter="false"
          @filter-change="onFilterChange"
        />
      </div>
      <ReportDownloadMenu
        class="flex-shrink-0"
        :disabled="isDownloadDisabled"
        :xlsx-handler="downloadXlsx"
      />
    </div>

    <!-- prettier-ignore -->
    <div>
    <section
      class="overflow-hidden bg-white border rounded-md dark:bg-slate-800 border-slate-100 dark:border-slate-700"
    >
      <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
        <h3 class="mb-0 text-lg font-medium text-slate-800 dark:text-slate-100">
          {{ $t('AGENT_REPORTS.COMPARISON.HEADER') }}
        </h3>
        <p class="mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{ $t('AGENT_REPORTS.COMPARISON.DESCRIPTION') }}
        </p>
      </div>

      <ReportLoadingContent
        v-slot="{ visibleLoading }"
        :is-loading="isFetching"
      >
        <woot-loading-state
          v-if="visibleLoading"
          class="px-4 py-12 text-xs"
          :message="$t('AGENT_REPORTS.COMPARISON.LOADING')"
        />

        <div
          v-else-if="!rows.length"
          class="px-4 py-12 text-sm text-center text-slate-600 dark:text-slate-300"
        >
          {{ $t('AGENT_REPORTS.COMPARISON.NO_RECORDS') }}
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th
                  class="sticky left-0 z-10 min-w-[240px] bg-slate-50 px-4 py-3 text-left text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                >
                  {{ $t('SUMMARY_REPORTS.AGENT') }}
                </th>
                <th
                  v-for="column in metricColumns"
                  :key="column"
                  class="min-w-[160px] bg-slate-50 px-4 py-3 text-right text-xs font-semibold uppercase text-slate-600 dark:bg-slate-900 dark:text-slate-300"
                >
                  {{ $t('SUMMARY_REPORTS.' + column) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.agent.id">
                <td
                  class="sticky left-0 z-10 border-t bg-white px-4 py-3 dark:bg-slate-800 border-slate-100 dark:border-slate-700"
                >
                  <button
                    type="button"
                    class="text-left hover:underline"
                    @click="$emit('agentSelect', row.agent)"
                  >
                    <UserAvatarWithName
                      :user="agentCellUser(row.agent)"
                      size="22px"
                      text-class="text-sm font-medium text-slate-800"
                      :show-link="false"
                    />
                  </button>
                </td>
                <td
                  class="border-t px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ row.conversationsCount.toLocaleString() }}
                </td>
                <td
                  class="border-t px-4 py-3 text-right text-sm text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ formattedDuration(row.avgFirstResponseTime) }}
                </td>
                <td
                  class="border-t px-4 py-3 text-right text-sm text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ formattedDuration(row.avgResolutionTime) }}
                </td>
                <td
                  class="border-t px-4 py-3 text-right text-sm text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ formattedDuration(row.avgReplyTime) }}
                </td>
                <td
                  class="border-t px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200 border-slate-100 dark:border-slate-700"
                >
                  {{ row.resolvedConversationsCount.toLocaleString() }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ReportLoadingContent>
    </section>
    </div>
  </div>
</template>
